import type { FacetOption } from '@/components/data-table/data-table-faceted-filter';
import type { QueueRow } from './selection';

export interface ReviewFilter {
  q?: string;
  /** projectId, chọn nhiều — rỗng/vắng = mọi dự án */
  project?: string[];
  /** Trạng thái dòng, chọn nhiều — rỗng/vắng = cả chờ duyệt lẫn đã duyệt */
  status?: ('submitted' | 'approved')[];
}

export const isFiltering = ({ q, project, status }: ReviewFilter) => (q ?? '').trim() !== '' || (project?.length ?? 0) > 0 || (status?.length ?? 0) > 0;

/** So khớp không dấu: NFD rồi bỏ dấu tổ hợp, đ/Đ → d, chữ thường — áp cho cả ô gõ lẫn dữ liệu. */
export const foldVi = (s: string) =>
  s.normalize('NFD').replace(/\p{M}/gu, '').replace(/[đĐ]/g, 'd').toLowerCase();

export function filterQueue(rows: QueueRow[], { q, project, status }: ReviewFilter): QueueRow[] {
  const needle = foldVi((q ?? '').trim());
  const picked = new Set(project ?? []);
  const states = new Set(status ?? []);
  if (!needle && picked.size === 0 && states.size === 0) return rows;
  return rows.filter(
    (r) =>
      (picked.size === 0 || picked.has(r.projectId)) &&
      (states.size === 0 || states.has(r.status ?? 'submitted')) &&
      (!needle || foldVi(`${r.userName} ${r.projectCode} ${r.projectName}`).includes(needle)),
  );
}

/** Lựa chọn cho bộ lọc Trạng thái, kèm số dòng mỗi trạng thái. */
export function statusOptions(rows: QueueRow[], leave: { status?: string }[] = []): FacetOption[] {
  const all = [...rows, ...leave];
  const approved = all.filter((r) => r.status === 'approved').length;
  return [
    { value: 'submitted', label: 'Chờ duyệt', count: all.length - approved },
    { value: 'approved', label: 'Đã duyệt', count: approved },
  ];
}

const GONE_LABEL = 'Dự án không còn trong hàng đợi';

/** Lựa chọn cho bộ lọc Dự án: mỗi dự án trong hàng đợi một lần ("mã · tên", số dòng), sắp theo mã.
 *  Dự án đang lọc trong URL mà không còn trong hàng đợi vẫn được liệt kê (số dòng 0) để bỏ chọn được. */
export function projectOptions(rows: QueueRow[], selected: string[] = []): FacetOption[] {
  const byId = new Map<string, { code: string; label: string; count: number }>();
  for (const r of rows) {
    const cur = byId.get(r.projectId);
    if (cur) cur.count += 1;
    else byId.set(r.projectId, { code: r.projectCode, label: `${r.projectCode} · ${r.projectName}`, count: 1 });
  }
  const live: FacetOption[] = [...byId.entries()]
    .sort(([ia, a], [ib, b]) => a.code.localeCompare(b.code, 'vi') || ia.localeCompare(ib))
    .map(([value, { label, count }]) => ({ value, label, count }));
  const gone: FacetOption[] = selected.filter((id) => !byId.has(id)).map((value) => ({ value, label: GONE_LABEL, count: 0 }));
  return [...live, ...gone];
}
